import AuthService from '../../services/AuthService';
import AnalyticsService from '../../services/AnalyticsService';
import { EVENTS } from '../../constants/analyticsEvents';

// LocalStorage'dan kullanıcı bilgilerini getir
const getUserFromStorage = () => {
  try {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error parsing user data from localStorage:', error);
    return null;
  }
};

const getPermissionsFromStorage = () => {
  try {
    const data = localStorage.getItem('permissions');
    return data ? JSON.parse(data) : [];
  } catch (error) {
    return [];
  }
};

const state = {
  user: getUserFromStorage(),
  isAuthenticated: AuthService.isTokenValid() || false,
  token: AuthService.getToken(),
  permissions: getPermissionsFromStorage(),
};

const mutations = {
  SET_USER(state, user) {
    state.user = user;
    // Kullanıcı bilgilerini localStorage'da sakla
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  },
  SET_AUTHENTICATED(state, status) {
    state.isAuthenticated = status;
    localStorage.setItem('isAuthenticated', status.toString());
  },
  SET_TOKEN(state, token) {
    state.token = token;
  },
  SET_PERMISSIONS(state, permissions) {
    state.permissions = Array.isArray(permissions) ? permissions : [];
    localStorage.setItem('permissions', JSON.stringify(state.permissions));
  },
  CLEAR_AUTH(state) {
    state.user = null;
    state.isAuthenticated = false;
    state.token = null;
    state.permissions = [];
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
  },
  // Yeni mutation: Sayfa yenilendiğinde auth state'i restore etmek için
  RESTORE_AUTH(state) {
    const user = getUserFromStorage();
    const isTokenValid = AuthService.isTokenValid();

    if (user && isTokenValid) {
      state.user = user;
      state.isAuthenticated = true;
      state.token = AuthService.getToken();
    } else {
      // Token geçersizse veya kullanıcı bilgisi yoksa temizle
      state.user = null;
      state.isAuthenticated = false;
      state.token = null;
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
    }
  },
};

const actions = {
  /**
   * Uygulama başlatıldığında auth state'i initialize et
   * @param {Object} context - Vuex context
   */
  initializeAuth({ commit, dispatch }) {
    const token = AuthService.getToken();
    const isTokenValid = AuthService.isTokenValid();
    const user = getUserFromStorage();

    if (token && isTokenValid && user) {
      // Token ve kullanıcı bilgileri geçerliyse auth state'i restore et
      commit('SET_USER', user);
      commit('SET_AUTHENTICATED', true);
      commit('SET_TOKEN', token);
      AnalyticsService.identify(user);
      // RBAC: izinleri /auth/me'den tazele
      dispatch('fetchMe');
    } else {
      // Geçersiz durumdaysa temizle
      commit('CLEAR_AUTH');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
    }
  },

  /**
   * User login operation
   * @param {Object} context - Vuex context
   * @param {Object} credentials - Login credentials
   */
  async login({ commit, dispatch }, credentials) {
    try {
      const { access_token, refresh_token } = await AuthService.login(credentials);

      // Token'ı set et
      commit('SET_TOKEN', access_token);

      // Token'dan kullanıcı bilgilerini extract et
      const tokenPayload = AuthService.getUserFromToken();

      if (!tokenPayload) {
        throw new Error('Invalid token payload');
      }

      // Token payload'ından user objesi oluştur
      const user = {
        id: tokenPayload.sub || tokenPayload.user_id || tokenPayload.id,
        email: tokenPayload.email || credentials.email,
        given_name: tokenPayload.given_name || tokenPayload.first_name,
        family_name: tokenPayload.family_name || tokenPayload.last_name,
        name: tokenPayload.name || `${tokenPayload.given_name || ''} ${tokenPayload.family_name || ''}`.trim() || tokenPayload.email || credentials.email,
        roles: tokenPayload.roles || tokenPayload.authorities || [],
        ...tokenPayload, // Diğer token bilgilerini de dahil et
      };

      commit('SET_USER', user);
      commit('SET_AUTHENTICATED', true);

      AnalyticsService.identify(user);
      AnalyticsService.track(EVENTS.USER_LOGGED_IN);

      // RBAC: izinleri /auth/me'den çek
      await dispatch('fetchMe');

      // Başarı bildirimi sayfa değiştikten sonra DashboardLayout'ta gösterilecek
      return { user, token: access_token };
    } catch (error) {
      commit('CLEAR_AUTH');

      dispatch('ui/showNotification', {
        type: 'error',
        message: error.message || 'Login failed!',
      }, { root: true });

      throw error;
    }
  },

  async signUp({ commit, dispatch }, credentials) {
    try {
      await AuthService.signUp(credentials);

      AnalyticsService.track(EVENTS.USER_SIGNED_UP);

      dispatch('ui/showNotification', {
        type: 'success',
        message: 'Successfully registered !',
      }, { root: true });

      return null;
    } catch (error) {
      dispatch('ui/showNotification', {
        type: 'error',
        message: error.message || 'Register failed!',
      }, { root: true });

      throw error;
    }
  },

  /**
   * User logout operation
   * @param {Object} context - Vuex context
   */
  async logout({ commit, dispatch }) {
    try {
      await AuthService.logout();
    } catch (error) {
      console.warn('Logout API error:', error);
    } finally {
      AnalyticsService.track(EVENTS.USER_LOGGED_OUT);
      AnalyticsService.reset();

      // Always clear local state
      commit('CLEAR_AUTH');
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      dispatch('ui/showNotification', {
        type: 'info',
        message: 'Successfully logged out.',
      }, { root: true });
    }
  },

  /**
   * Token ile kullanıcı bilgilerini getir
   * @param {Object} context - Vuex context
   */
  async fetchUser({ commit, dispatch, state }) {
    try {
      if (!state.token || !AuthService.isTokenValid()) {
        throw new Error('Invalid token');
      }

      // Token'dan kullanıcı bilgilerini extract et
      const tokenPayload = AuthService.getUserFromToken();

      if (!tokenPayload) {
        throw new Error('Invalid token payload');
      }

      // Token payload'ından user objesi oluştur
      const user = {
        id: tokenPayload.sub || tokenPayload.user_id || tokenPayload.id,
        email: tokenPayload.email,
        given_name: tokenPayload.given_name || tokenPayload.first_name,
        family_name: tokenPayload.family_name || tokenPayload.last_name,
        name: tokenPayload.name || `${tokenPayload.given_name || ''} ${tokenPayload.family_name || ''}`.trim() || tokenPayload.email,
        roles: tokenPayload.roles || tokenPayload.authorities || [],
        ...tokenPayload, // Diğer token bilgilerini de dahil et
      };

      commit('SET_USER', user);
      return user;
    } catch (error) {
      dispatch('logout');
      throw error;
    }
  },

  /**
   * /auth/me ile profil, rol ve izinleri çek; permission'ları store'a yaz (RBAC)
   * @param {Object} context - Vuex context
   */
  async fetchMe({ commit, state }) {
    if (!state.token || !AuthService.isTokenValid()) {
      return null;
    }
    try {
      const me = await AuthService.me();
      commit('SET_PERMISSIONS', me.permissions || []);
      // Profilden gelen güncel rol/isim bilgisini de mevcut user ile birleştir
      const mergedUser = { ...(state.user || {}), ...me };
      commit('SET_USER', mergedUser);
      return me;
    } catch (error) {
      console.error('fetchMe error:', error);
      return null;
    }
  },

  /**
   * Token yenile
   * @param {Object} context - Vuex context
   */
  async refreshToken({ commit, dispatch }) {
    try {
      const token = await AuthService.refreshToken();
      commit('SET_TOKEN', token);
      return token;
    } catch (error) {
      dispatch('logout');
      throw error;
    }
  },

  /**
   * Profil güncelle
   * @param {Object} context - Vuex context
   * @param {Object} profileData - Profil verileri
   */
  async updateProfile({ commit, dispatch }, profileData) {
    try {
      const user = await AuthService.updateProfile(profileData);
      commit('SET_USER', user);

      dispatch('ui/showNotification', {
        type: 'success',
        message: 'Profil başarıyla güncellendi!',
      }, { root: true });

      return user;
    } catch (error) {
      dispatch('ui/showNotification', {
        type: 'error',
        message: error.message || 'Profil güncellenirken hata oluştu!',
      }, { root: true });

      throw error;
    }
  },

  /**
   * Şifre değiştir
   * @param {Object} context - Vuex context
   * @param {Object} passwordData - Şifre verileri
   */
  async changePassword({ dispatch }, passwordData) {
    try {
      await AuthService.changePassword(passwordData);

      dispatch('ui/showNotification', {
        type: 'success',
        message: 'Şifre başarıyla değiştirildi!',
      }, { root: true });
    } catch (error) {
      dispatch('ui/showNotification', {
        type: 'error',
        message: error.message || 'Şifre değiştirilirken hata oluştu!',
      }, { root: true });

      throw error;
    }
  },
};

const getters = {
  isAuthenticated: (state) => state.isAuthenticated,
  user: (state) => state.user,
  /** Token'dan gelen roles string[] içinde superuser var mı */
  isSuperuser: (state) => {
    const roles = state.user?.roles || [];
    return roles.some((r) => String(r).toLowerCase() === 'superuser');
  },
  /** /auth/me'den gelen izin listesi (örn: ['read:user', 'write:role']) */
  permissions: (state) => state.permissions || [],
  /** Belirli bir izne sahip mi? superuser her zaman true. */
  hasPermission: (state, getters) => (permission) => {
    if (getters.isSuperuser) return true;
    if (!permission) return true;
    return (state.permissions || []).includes(permission);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
